import React, { FC, useEffect, useState } from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField } from '@material-ui/core';
import { createUseStyles } from 'src/theming';
import { CollaborativeSource, CreateAppointmentOptions } from 'src/interfaces/api/Collaborative';
import { Controller, useForm } from 'react-hook-form';
import AppointmentTypeSelect from 'src/components/ApiField/AppointmentTypeSelect';
import { CriticitySelect } from 'src/components/ApiField';
import MomentUtils from '@date-io/moment';
import { MuiPickersUtilsProvider } from '@material-ui/pickers';
import EmployeeSelect from 'src/components/ApiField/EmployeeSelect';
import DateTimePicker from 'src/components/DateTimePicker';
import moment from 'moment';

const useStyles = createUseStyles({
  title: {
    width: 400,
    '& h2': {
      color: 'rgba(0, 0, 0, 0.87)',
    },
  },
  error: {
    color: '#f44336',
    fontSize: '.75rem',
    padding: '0 25px',
  },
  formControl: {
    marginBottom: '16px !important',
  },
});

const defaultFormValues = {
  duration: 0,
  employeesIds: [],
  meetingDate: moment().format('YYYY-MM-DD HH:mm:ss'),
  title: '',
  place: '',
  text: '',
};

interface Props {
  appointmentSource?: CollaborativeSource;
  open: boolean;
  onClose: () => void;
  onValidate: (createAppointmentOptions: CreateAppointmentOptions) => void;
}

const AddAppointmentDialog: FC<Props> = ({ open, appointmentSource, onClose, onValidate }) => {
  const classes = useStyles();

  const [formError, setFormError] = useState<string | undefined>();

  const { reset, control, handleSubmit } = useForm<CreateAppointmentOptions>({
    defaultValues: {
      ...defaultFormValues,
      attachedTo: appointmentSource,
    },
  });

  useEffect(() => {
    setFormError(undefined);
  }, []);

  const onSubmit = async (createAppointmentOptions: CreateAppointmentOptions) => {
    try {
      setFormError(undefined);
      await onValidate(createAppointmentOptions);
      reset(defaultFormValues);
      onClose();
    } catch (e) {
      setFormError(e.message);
    }
  };

  return (
    <Dialog onClose={onClose} scroll='paper' open={open}>
      <DialogTitle className={classes.title}>Créer un rendez-vous</DialogTitle>

      <DialogContent>
        <MuiPickersUtilsProvider utils={MomentUtils}>
          <Controller
            name='title'
            control={control}
            rules={{ required: { value: true, message: 'Le champ est requis' } }}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                className={classes.formControl}
                label='Titre'
                placeholder='Retrouver le rdv plus tard'
                variant='outlined'
                fullWidth
                error={undefined !== error}
                helperText={error?.message}
              />
            )}
          />

          <Controller
            name='text'
            control={control}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                className={classes.formControl}
                label='Texte'
                placeholder='Description du rdv (facultatif)'
                variant='outlined'
                multiline
                minRows={3}
                fullWidth
                error={undefined !== error}
                helperText={error?.message}
              />
            )}
          />

          <Controller
            name='appointmentTypeId'
            control={control}
            rules={{ required: { value: true, message: 'Le champ est requis' } }}
            render={({ field, fieldState: { error } }) => (
              <AppointmentTypeSelect
                {...field}
                className={classes.formControl}
                fullWidth
                error={undefined !== error}
                helperText={error?.message}
              />
            )}
          />

          <Controller
            name='criticity'
            control={control}
            rules={{ required: { value: true, message: 'Le champ est requis' } }}
            render={({ field, fieldState: { error } }) => (
              <CriticitySelect
                {...field}
                className={classes.formControl}
                fullWidth
                error={undefined !== error}
                helperText={error?.message}
              />
            )}
          />

          <Controller
            name='place'
            control={control}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                className={classes.formControl}
                label='Lieu'
                placeholder='Lieu du rdv (facultatif)'
                variant='outlined'
                fullWidth
                error={undefined !== error}
                helperText={error?.message}
              />
            )}
          />

          <Controller
            name='meetingDate'
            control={control}
            rules={{ required: { value: true, message: 'Le champ est requis' } }}
            render={({ field: { onChange, ...fieldOthers }, fieldState: { error } }) => (
              <DateTimePicker
                {...fieldOthers}
                onChange={(date) => onChange(date ? date.format('YYYY-MM-DD HH:mm:ss') : undefined)}
                className={classes.formControl}
                label='Date du rendez-vous'
                format='DD/MM/YYYY HH:mm'
                inputVariant='outlined'
                helperText={error?.message}
                error={undefined !== error}
                disablePast
                fullWidth
                ampm={false}
              />
            )}
          />

          <Controller
            name='duration'
            control={control}
            rules={{
              required: { value: true, message: 'Le champ est requis' },
              min: { value: 1, message: 'La durée doit être superieur à 0' },
            }}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                className={classes.formControl}
                label='Durée prévue (en min)'
                type='number'
                variant='outlined'
                InputProps={{ inputProps: { min: 0 } }}
                fullWidth
                error={undefined !== error}
                helperText={error?.message}
              />
            )}
          />

          <Controller
            name='employeesIds'
            control={control}
            rules={{ required: { value: true, message: 'Le champ est requis' } }}
            render={({ field, fieldState: { error } }) => (
              <EmployeeSelect
                {...field}
                label='Employés associés'
                className={classes.formControl}
                multiple
                error={undefined !== error}
                helperText={error?.message}
                fullWidth
              />
            )}
          />
        </MuiPickersUtilsProvider>
      </DialogContent>

      <DialogActions>
        <Button type='button' onClick={onClose} color='primary'>
          Annuler
        </Button>

        <Button type='submit' color='primary' onClick={handleSubmit(onSubmit)}>
          Créer
        </Button>
      </DialogActions>

      {formError && <p className={classes.error}>{formError}</p>}
    </Dialog>
  );
};

export default AddAppointmentDialog;
